import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../api/axios";

// Auth rides the axios interceptor (auth.access_token), same as every other page
// in this panel — nothing to pass by hand.
const KYC = "/api/admin/kyc";

const root = ["kyc"];

// endDate is compared as an instant, not a day: a bare 2026-09-07 parses to
// midnight, so picking the same day for both bounds returns nothing. Stretch it to
// the end of that UTC day. startDate is already the lower bound we want.
const endOfDay = (d) => (d ? `${d}T23:59:59.999Z` : undefined);

// Envelope is the standard Ginox page: { data, currentPage, totalPages, totalRows }.
// An empty status or docType means "all"; an unrecognised one is a 400, so blanks
// are dropped rather than sent.
const fetchKycList = (f) =>
  axiosInstance
    .get(`${KYC}/list`, {
      params: {
        page: f.page,
        pageSize: f.pageSize,
        order: f.order,
        ...(f.username?.trim() ? { username: f.username.trim() } : {}),
        ...(f.status ? { status: f.status } : {}),
        ...(f.docType ? { docType: f.docType } : {}),
        ...(f.supplement ? { supplement: f.supplement } : {}),
        ...(f.startDate ? { startDate: f.startDate } : {}),
        ...(f.endDate ? { endDate: endOfDay(f.endDate) } : {}),
      },
    })
    .then((r) => r.data.data);

// Page-based, not cursor: this API answers currentPage / totalPages / totalRows.
export function useKycList(filters) {
  return useQuery({
    queryKey: [...root, "list", filters],
    queryFn: () => fetchKycList(filters),
    refetchOnWindowFocus: true, // live work list
    staleTime: 0,
  });
}

// onSettled, not onSuccess: if another admin decided this row first the call fails,
// and the stale list still has to be dropped and refetched.
const useDecision = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [...root, "list"] }),
  });
};

export const useApproveKyc = () =>
  useDecision((id) => axiosInstance.post(`${KYC}/approve`, { id }));

// Capital R is the real route. Free text reason, no enum.
export const useRejectKyc = () =>
  useDecision(({ id, reason }) =>
    axiosInstance.post(`${KYC}/Reject`, { id, reason }),
  );

// Only rejected -> pending. The backend refuses anything else (and a user with a
// newer submission) with a readable 400, so the caller just toasts the message.
export const useReopenKyc = () =>
  useDecision((id) => axiosInstance.post(`${KYC}/reopen`, { id }));

// Decides ONLY the proof of address and selfie an already-approved user sent after
// the fact. status stays approved and kycApproved stays true either way. Reason is
// required when rejecting (400 otherwise) and is shown to the user verbatim.
export const useDecideSupplement = () =>
  useDecision(({ id, decision, reason }) =>
    axiosInstance.post(`${KYC}/supplement/decide`, { id, decision, reason }),
  );

// Supplements sit on APPROVED records, so a status=pending queue never shows them.
export const SUPPLEMENT_OPTIONS = [
  { value: "", label: "Any" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

// Both filters reject anything outside these sets with a 400.
export const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export const DOC_TYPE_OPTIONS = [
  { value: "", label: "All documents" },
  { value: "id_card", label: "ID card" },
  { value: "driving_license", label: "Driving license" },
  { value: "passport", label: "Passport" },
];

// The backend takes free text, so `label` is what gets sent — `value` only keys the
// radio list. OTHER swaps the list for a textarea.
export const REJECTION_REASONS = [
  { value: "BLURRY_OR_DARK", label: "Photo too blurry or too dark" },
  { value: "GLARE", label: "Glare hides part of the document" },
  { value: "OUT_OF_FRAME", label: "Part of the document is out of frame or covered" },
  { value: "WRONG_SIDE", label: "Wrong side uploaded, or the same side twice" },
  { value: "BACK_MISSING", label: "Back of document missing" },
  { value: "EXPIRED", label: "Document expired" },
  { value: "WRONG_DOCUMENT_TYPE", label: "Wrong document type" },
  { value: "NOT_ORIGINAL", label: "Not the original document, a screenshot or a photocopy" },
  { value: "DETAILS_MISMATCH", label: "Details do not match the document" },
  {
    value: "NAME_MISMATCH",
    label: "The first and last name on the account do not match the name on the submitted identity document",
  },
  { value: "ADDRESS_UNCLEAR", label: "Proof of address is unclear, expired or not in your name" },
  { value: "ADDRESS_INVALID", label: "The proof of address document is invalid." },
  { value: "SELFIE_UNCLEAR", label: "Selfie is unclear, or your face or ID is not visible" },
  { value: "SELFIE_INVALID", label: "The selfie is invalid." },
  { value: "HANDWRITTEN_TEXT", label: "Handwritten confirmation is missing or its name, account ID or date does not match" },
  { value: "OTHER", label: "Other" },
];

// A supplement is only the address document and the selfie, so only the reasons
// that can apply to those two (the selfie holds an ID, so a name mismatch can too).
export const SUPPLEMENT_REJECTION_REASONS = REJECTION_REASONS.filter((r) =>
  [
    "NAME_MISMATCH",
    "ADDRESS_UNCLEAR",
    "ADDRESS_INVALID",
    "SELFIE_UNCLEAR",
    "SELFIE_INVALID",
    "HANDWRITTEN_TEXT",
    "OTHER",
  ].includes(r.value),
);
